import { AttributesMixin, define, Linked, Store } from "@type-r/models";
import { pureRenderProps, useIO, useLinked, useModel } from "@type-r/react";
import { CompanyModel, globalStore } from "api";
import { Activity } from "api/activity";
import { CompactTextArea, Flex, Loading, NavTree, NavTreeItem, PageLayout, PopupIODialog, SelectFile } from "lqx-react";
import * as React from "react";
import { InmatchConversation, Payment, Reconciliation, uploadPaymentFile } from "src/app/pages/inmatch/api";
import { PaymentHeader } from "./header";
import { PaymentInformation } from "./paymentInformation";

interface State extends AttributesMixin<typeof State>{}
@define class State extends Store {
    static attributes = {
        payment : Payment,
        companies : [ CompanyModel ],
        reconciliations : [ Reconciliation ],
        activities : [Activity]
    };

    async fetch(){
        this.companies = globalStore.companies;
        
        await Promise.all([
            this.payment.fetch(),
            this.activities.fetch({params: {assetId: this.payment.id, assetType: 'PaymentInfo'}}),
            this.reconciliations.fetch({params: { paymentId: this.payment.id }}),
            this.payment.uploads.fetch(),
        ]);

        await Promise.all(this.reconciliations.map(x => x.fetch()));

        return this;
    }
}


export const PaymentDetails = ({ paymentId }) => {
    const state = useModel(State);

    const isReady = useIO(
      async () => {
          state.payment.id = paymentId;
          await state.fetch();
      },
      [paymentId]
    );

    const $dialog = useLinked<boolean>(false);

    const refreshActivities = async () => {
        await Promise.all([
            state.payment.uploads.fetch(),
            state.activities.fetch({params: {assetId: paymentId, assetType: 'PaymentInfo'}})
        ]);
    };

    return isReady ? (
        <PageLayout
            menu={<Menu />}
            header={<PaymentHeader payment={state.payment} reconciliations={state.reconciliations} $dialog={$dialog} />}
            subheader={-90}
            maxWidth="xl"
        >
            <PaymentInformation payment={state.payment} reconciliations={state.reconciliations} activities={state.activities} 
                uploads={state.payment.uploads}
            />

            { $dialog.value ?  <UploadPaymentFileDialog $isOpen={$dialog} paymentId={paymentId} onSuccess={refreshActivities} /> : undefined }
        </PageLayout>
    ) : (
        <Loading />
    );
};

const Menu = pureRenderProps({}, () => (
    <NavTree
        defaultExpanded={[
            "payment-information",
        ]}
    >
        <NavTreeItem label="Payment Details" target="payment-information">
          <NavTreeItem label="Payment Information" target="payment-info" />
        </NavTreeItem>
        <NavTreeItem label="Payor" target="customer">
          <NavTreeItem label="Profile" target="customer-profile" />
          <NavTreeItem label="Bank Details" target="customer-bank-details" />
        </NavTreeItem>
        <NavTreeItem label="Payee" target="supplier">
          <NavTreeItem label="Profile" target="supplier-profile" />
          <NavTreeItem label="Bank Details" target="supplier-bank-details" />
        </NavTreeItem>
        <NavTreeItem label="Additional Information" target="additional-information">
          <NavTreeItem label="Documents" target="documents" />
          <NavTreeItem label="All Activity" target="all-activity" />
        </NavTreeItem>
    </NavTree>
    )
  );

export const UploadPaymentFileDialog = ({ $isOpen, paymentId, onSuccess } : {
    $isOpen : Linked<boolean>,
    paymentId : Number,
    onSuccess: () => void
}) => {

    const $comment = useLinked('');
    const $file = useLinked(null);
    
    return (
        <PopupIODialog
            title='Upload file'
            actionLabel='Upload file'
            $isOpen={ $isOpen }
            success="File uploaded"
            onSuccess={onSuccess}
            disabled={ !$file.value }
            doIO={ async () => {

                const updated = await uploadPaymentFile(paymentId, $file.value, $comment.value);
            }}>
                <Flex column>
                    <CompactTextArea autoFocus label="Comment" rows={5} $value={ $comment }/>
                    <SelectFile accept=".csv,.edi,.xml,.txt,.json,.pdf,.xls,.doc,.docx,.xlsx,.png,.jpeg,.msg" label="ATTACHMENT" $value={ $file } />

                </Flex>
        </PopupIODialog>
    );
}
